/* eslint-disable class-methods-use-this */
import cookies from 'js-cookie';

import { ExceptionDimensions, RequestHeaderKey, RequestHeaderValue, ResponseCode, logAnalyticsEvent, paramsToObject, sleep } from '@vestico/utils';

import { ClientDetails, ClientExperiments, defaultCookieAttributes } from '@vestico/model';

import { AuthInfo, AuthorizedRequestInfo, ProductConfiguration, authInfoIsValid, getProductConfigUIDs } from '../model/api';

const vesticoInternalCookieKey = 'co.vestico.internal';
const vesticoDeveloperPreviewCookieKey = 'co.vestico.developerPreview';
const vesticoFineTuneCookieKey = 'co.vestico.fineTune';
const vesticoQueryParameterKeyInternal = '_vwopts';

const WIDGET_API_URL = process.env.WIDGET_API_URL as string;

export const vesticoAuthCookieKey = 'co.vestico.auth';

const getValidAuth = async (authRequestInfo: AuthorizedRequestInfo) => {
  const { authInfo, resetAuth } = authRequestInfo;
  if (!authInfoIsValid(authInfo)) {
    authRequestInfo.authInfo = await resetAuth();
  }

  return authRequestInfo.authInfo;
};

export interface FetchRequestOptions {
  authRequestInfo?: AuthorizedRequestInfo;
  method?: 'GET' | 'POST' | 'PUT';
  // skipcq JS-0323
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: { [x: string]: any };
  iOSBundleIdentifier?: string;
  additionalHeaders?: { [x: string]: string };
  keepalive?: boolean;
}

export interface FetchRequest extends FetchRequestOptions {
  url: string;
}

const widgetRequestOptions = async ({
  authRequestInfo,
  method,
  body,
  iOSBundleIdentifier,
  additionalHeaders,
  keepalive,
}: FetchRequestOptions): Promise<RequestInit> => {
  let headers: HeadersInit = {
    [RequestHeaderKey.contentType]: RequestHeaderValue.contentTypeJSON,
    [RequestHeaderKey.uri]: document.location.toString(),
  };

  if (authRequestInfo) {
    const authInfo = await getValidAuth(authRequestInfo);

    if (!authInfo) {
      throw new Error('Cannot get valid auth');
    }

    headers[RequestHeaderKey.authorization] = `Bearer ${authInfo.token}`;
  }
  if (iOSBundleIdentifier) {
    headers[RequestHeaderKey.iOSBundleIdentifier] = iOSBundleIdentifier;
  }

  if (additionalHeaders) {
    headers = { ...headers, ...additionalHeaders };
  }

  const options: RequestInit = {
    mode: 'cors',
    headers,
    body: body ? JSON.stringify(body) : undefined,
    method: method ?? 'GET',
    keepalive,
  };
  return options;
};

export const fetchRequest = async ({ url, authRequestInfo, method, body, iOSBundleIdentifier, additionalHeaders, keepalive }: FetchRequest) => {
  const options = await widgetRequestOptions({ authRequestInfo, method, body, iOSBundleIdentifier, additionalHeaders, keepalive });

  console.debug(`Fetching ${url}`);
  let result = await fetch(url, options).catch(async (e) => {
    // We retry once more when an exception happens

    console.error(e.message);
    logAnalyticsEvent({
      category: 'exception',
      event: 'fetch-request-generic',
      name: e.message,
    });

    if (authRequestInfo) {
      authRequestInfo.authInfo = await authRequestInfo.resetAuth();
      const newOptions = await widgetRequestOptions({ authRequestInfo, method, body, iOSBundleIdentifier, additionalHeaders, keepalive });
      const res = await fetch(url, newOptions);
      return res;
    }

    throw new Error(e);
  });

  // Let's retry after one second if the cloud backend returns an internal error or timeout
  // This can happen during fast scale ups
  if (result.status === ResponseCode.internalError || result.status === ResponseCode.timeout || result.status === ResponseCode.instanceError) {
    await sleep(1000);
    result = await fetch(url, options);
  }

  // Refetch auth info automatically
  if (result.status === ResponseCode.unauthorized) {
    logAnalyticsEvent({
      category: 'exception',
      event: '401_returned_api',
    });

    if (authRequestInfo) {
      authRequestInfo.authInfo = await authRequestInfo.resetAuth();
      const newOptions = await widgetRequestOptions({ authRequestInfo, method, body, iOSBundleIdentifier, additionalHeaders, keepalive });
      result = await fetch(url, newOptions);
    }
  }

  return result;
};

export function getUserAgentIsRobot() {
  const re = /bot|google|baidu|bing|msn|teoma|slurp|yandex|spider|crawl|facebookexternalhit|headlesschrome/i;
  const { userAgent } = navigator;
  if (re.test(userAgent)) {
    console.error('client has been identified as crawler');
    return true;
  }
  return false;
}

const userAgentIsRobot = getUserAgentIsRobot();

interface VesticoQueryOptions {
  internal?: boolean;
  devPreview?: boolean;
  fineTune?: boolean;
}

const getVesticoQueryOptions = (): VesticoQueryOptions => {
  const params = paramsToObject(document.location.search);
  const vesticoQueryParams = params[vesticoQueryParameterKeyInternal];
  if (vesticoQueryParams) {
    if (vesticoQueryParams === 'ft') {
      return { devPreview: true, fineTune: true };
    }
    if (vesticoQueryParams === 'qa') {
      return { internal: true };
    }
    if (vesticoQueryParams === 'dp') {
      return { devPreview: true };
    }
  }

  return {};
};

let _internal: boolean | undefined;
export const getIsInternal = () => {
  if (_internal === undefined) {
    const string = cookies.get(vesticoInternalCookieKey);

    if (!string) {
      const { internal } = getVesticoQueryOptions();
      if (internal) {
        const attributes = defaultCookieAttributes();
        attributes.expires = 365;
        cookies.set(vesticoInternalCookieKey, 'true', attributes);
      }

      _internal = !!internal;
    } else {
      _internal = string === 'true';
    }
  }

  return _internal;
};

let _developerPreview: boolean | undefined;
export const getIsDeveloperPreview = () => {
  if (_developerPreview === undefined) {
    const devPreviewString = cookies.get(vesticoDeveloperPreviewCookieKey);

    if (!devPreviewString) {
      const { devPreview } = getVesticoQueryOptions();
      if (devPreview) {
        const attributes = defaultCookieAttributes();
        attributes.expires = 365;
        cookies.set(vesticoDeveloperPreviewCookieKey, 'true', attributes);
      }

      _developerPreview = !!devPreview;
    } else {
      _developerPreview = devPreviewString === 'true';
    }
  }

  return _developerPreview;
};

let _fineTuneEnabled: boolean | undefined;
export const getIsFineTuneEnabled = () => {
  if (_fineTuneEnabled === undefined) {
    const fineTuneString = cookies.get(vesticoFineTuneCookieKey);

    if (!fineTuneString) {
      const { fineTune } = getVesticoQueryOptions();
      if (fineTune) {
        const attributes = defaultCookieAttributes();
        attributes.expires = 365;
        cookies.set(vesticoFineTuneCookieKey, 'true', attributes);
      }

      _fineTuneEnabled = !!fineTune;
    } else {
      _fineTuneEnabled = fineTuneString === 'true';
    }
  }

  return _fineTuneEnabled;
};

/*
 *
 * URL GENERATION METHODS
 *
 */
export function fetchTokenRequestURL(clientId: string, isRefetch: boolean, isSDK?: boolean, sdkAPIKey?: string): string | null {
  if (userAgentIsRobot) {
    return null;
  }

  const urlString = process.env.GET_AUTH_URL as string;
  if (!urlString) {
    console.error('GET_AUTH_URL missing from .env');
    logAnalyticsEvent({
      category: 'exception',
      event: 'token-reqest-url-precondition',
      name: 'Missing GET_AUTH_URL',
      dimensions: { provider: clientId } as ExceptionDimensions,
    });
    return null;
  }

  const apiKey = process.env.GET_AUTH_API_KEY as string;
  if (!apiKey) {
    console.error('GET_AUTH_API_KEY missing from .env');
    logAnalyticsEvent({
      category: 'exception',
      event: 'token-reqest-url-precondition',
      name: 'Missing GET_AUTH_API_KEY',
      dimensions: { provider: clientId } as ExceptionDimensions,
    });
    return null;
  }

  if (clientId.length === 0) {
    console.error('Empty client id not supported');
    logAnalyticsEvent({
      category: 'exception',
      event: 'token-reqest-url-precondition',
      name: 'Missing client id',
      dimensions: { provider: clientId } as ExceptionDimensions,
    });
    return null;
  }

  const url = new URL(`${urlString}/token`);
  url.searchParams.append('client', clientId);

  // Add additional debug info for refetch scenario
  if (isRefetch) {
    url.searchParams.append('isRefetch', `${isRefetch}`);
    url.searchParams.append('clientDate', `${Date.now()}`);

    const existingAuthInfoString = cookies.get(vesticoAuthCookieKey);
    if (existingAuthInfoString && existingAuthInfoString !== undefined) {
      const existingAuthInfo: AuthInfo = JSON.parse(existingAuthInfoString);
      if (existingAuthInfo) {
        url.searchParams.append('cookieExpiry', `${existingAuthInfo.expiryDate}`);
      }
    }
  }

  if (isSDK) {
    if (!sdkAPIKey) {
      console.error('Missing required SDK API Key');
      return null;
    }
    url.searchParams.append('isSDK', `${isSDK}`);
    url.searchParams.append('sdkAPIKey', `${sdkAPIKey}`);
  }

  url.searchParams.append('key', apiKey);
  return url.toString();
}

export const getJWTTokenExpiry = (token: string) => {
  const expiry = JSON.parse(window.atob(token.split('.')[1])).exp;
  return expiry;
};

export const createAPIRequestURL = async (authRequestInfo: AuthorizedRequestInfo, apiEndpoint: string) => {
  if (userAgentIsRobot) {
    return null;
  }

  const authInfo = await getValidAuth(authRequestInfo);

  if (!authInfo) {
    return null;
  }

  const url = new URL(`${WIDGET_API_URL}${apiEndpoint}`);
  url.searchParams.append('key', authInfo.apiKey);

  return url;
};

export const createProductRequest = async (
  productConfig: ProductConfiguration | ProductConfiguration[],
  clientId: string,
  userUID: string | undefined,
  authRequestInfo: AuthorizedRequestInfo,
  isPreview: boolean,
  isSDK: boolean,
  clientExperiments?: ClientExperiments
) => {
  if (userAgentIsRobot) {
    return null;
  }

  const productUIDs = getProductConfigUIDs(productConfig);
  if (!productUIDs || productUIDs.length === 0 || !clientId) {
    console.error(`productRequest missing required entries: productConfig: ${JSON.stringify(productConfig)}, clientId ${clientId}`);
    logAnalyticsEvent({
      category: 'exception',
      event: 'product-reqest-url-precondition',
      name: 'Missing required entries',
      dimensions: { provider: clientId, description: JSON.stringify(productConfig) } as ExceptionDimensions,
    });
    return null;
  }

  const url = await createAPIRequestURL(authRequestInfo, '/product-info');
  if (!url) {
    return null;
  }

  url.searchParams.append('products', JSON.stringify(productUIDs));
  url.searchParams.append('client', clientId);

  if (userUID) {
    url.searchParams.append('user', userUID);
  }

  if (isPreview) {
    url.searchParams.append('isPreview', 'true');
  }

  if (getIsInternal()) {
    url.searchParams.append('isInternal', 'true');
  }

  if (isSDK) {
    url.searchParams.append('isSDK', 'true');
  }

  if (clientExperiments) {
    url.searchParams.append('experiments', JSON.stringify(clientExperiments));
  }

  return url.toString();
};

const vesticoHostInfoKey = 'co.vestico.hostInfo';
const HOST_INFO_DELAY = 60 * 60 * 1000;

export async function getHostIPAddress() {
  const lastIPAddress: string | undefined = cookies.get(vesticoHostInfoKey);
  if (lastIPAddress) {
    return lastIPAddress;
  }

  let ipAddress: string | undefined;
  try {
    const options: RequestInit = {
      headers: {
        [RequestHeaderKey.contentType]: RequestHeaderValue.contentTypeJSON,
      },
      method: 'GET',
    };
    const res = await fetch('https://api.bigdatacloud.net/data/client-ip', options);
    ipAddress = (await res.json()).ipString;
  } catch (error) {
    console.error(error);
    logAnalyticsEvent({
      category: 'exception',
      event: 'get-ip req',
      name: error.message,
    });
    return undefined;
  }
  if (ipAddress) {
    const attributes = defaultCookieAttributes();
    attributes.expires = new Date(Date.now() + HOST_INFO_DELAY);
    cookies.set(vesticoHostInfoKey, ipAddress, attributes);
  }

  return ipAddress;
}

export const getClientDetails = async (): Promise<ClientDetails> => {
  const width = window.innerWidth || document.body.clientWidth;
  const height = window.innerHeight || document.body.clientHeight;
  const ipAddress = await getHostIPAddress();

  const clientDetails: ClientDetails = {
    height,
    width,
    ipAddress,
  };

  if (getIsInternal()) {
    clientDetails.internal = true;
  }
  return clientDetails;
};
